import { useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { Icon } from "@avds/icons";
import { authMode, login, setMockSession, MOCK_ROLES } from "@/lib/auth";
import "./login.css";

interface LoginSearch {
  redirect?: string;
  error?: string;
}

export const Route = createFileRoute("/login")({
  validateSearch: (search: Record<string, unknown>): LoginSearch => ({
    redirect: typeof search.redirect === "string" ? search.redirect : undefined,
    error: typeof search.error === "string" ? search.error : undefined,
  }),
  component: LoginPage,
});

function LoginPage() {
  const { t } = useTranslation();
  const { redirect, error } = Route.useSearch();
  const [email, setEmail] = useState("");
  const [roles, setRoles] = useState<string[]>(["admin"]);
  const isMock = authMode() === "mock";

  // Internal employees only. entra: hand off to the API BFF (OAuth → session
  // cookie). mock: store the dev session locally, then a full navigation so the
  // _shell guard re-runs fetchSession cleanly. Partners use the separate portal.
  function signInInternal() {
    if (!isMock) {
      login();
      return;
    }
    if (!email || roles.length === 0) return;
    setMockSession({ email, roles });
    window.location.href = redirect ?? "/dashboard";
  }

  function toggleRole(role: string) {
    setRoles((prev) =>
      prev.includes(role) ? prev.filter((r) => r !== role) : [...prev, role],
    );
  }

  return (
    <div className="login-wrap" data-screen-label="Back-office Login">
      <div className="login-panel">
        <div className="login-card">
          <img className="login-mark" src="/mark.png" alt={t("login.brandAlt")} />
          <h1 className="t-h1" style={{ marginTop: "var(--space-5)", color: "var(--green-700)" }}>
            {t("login.title")}
          </h1>
          <p
            style={{
              color: "var(--text-secondary)",
              marginTop: "var(--space-1)",
              fontSize: "var(--fs-md)",
            }}
          >
            {t("login.subtitle")}
          </p>

          {error && (
            <div className="avds-alert avds-alert--danger login-error" role="alert">
              <Icon name="alert-circle" size={16} />
              <div>{t("login.signInFailed")}</div>
            </div>
          )}

          {/* ── Internal employees → SSO (dev login in mock mode) ── */}
          <section className="login-block">
            <div className="login-block__head">
              <h2 className="login-block__title">{t("login.internalTitle")}</h2>
              <p className="login-block__sub">{t("login.internalSubtitle")}</p>
            </div>

            {isMock ? (
              <form
                className="login-form"
                onSubmit={(e) => {
                  e.preventDefault();
                  signInInternal();
                }}
              >
                <div className="avds-field">
                  <label className="avds-field__label" htmlFor="login-email">
                    {t("login.email")}
                  </label>
                  <input
                    id="login-email"
                    className="avds-input"
                    type="email"
                    placeholder="naam@albertvdscheur.nl"
                    autoComplete="off"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                  />
                </div>
                <div className="login-dev">
                  <div className="login-dev__title">{t("login.devMode")}</div>
                  <div className="login-dev__roles">
                    {MOCK_ROLES.map((role) => (
                      <label key={role}>
                        <input
                          type="checkbox"
                          checked={roles.includes(role)}
                          onChange={() => toggleRole(role)}
                        />
                        {role}
                      </label>
                    ))}
                  </div>
                </div>
                <button type="submit" className="avds-btn avds-btn--primary avds-btn--lg login-submit">
                  {t("login.signIn")}
                </button>
              </form>
            ) : (
              <button
                type="button"
                className="avds-btn avds-btn--primary avds-btn--lg login-submit"
                onClick={signInInternal}
              >
                <Icon name="lock" size={16} /> {t("login.sso")}
              </button>
            )}
          </section>

          <div className="login-sep" role="separator" aria-hidden="true" />

          {/* ── Partners → partner portal (email + password; not live yet) ── */}
          <section className="login-block">
            <div className="login-block__head">
              <h2 className="login-block__title">{t("login.partnerTitle")}</h2>
              <p className="login-block__sub">{t("login.partnerSubtitle")}</p>
            </div>
            <form className="login-form" onSubmit={(e) => e.preventDefault()}>
              <div className="avds-field">
                <label className="avds-field__label" htmlFor="partner-email">
                  {t("login.email")}
                </label>
                <input
                  id="partner-email"
                  className="avds-input"
                  type="email"
                  placeholder="naam@bedrijf.nl"
                  autoComplete="off"
                  disabled
                />
              </div>
              <div className="avds-field">
                <label className="avds-field__label" htmlFor="partner-password">
                  {t("login.password")}
                </label>
                <input
                  id="partner-password"
                  className="avds-input"
                  type="password"
                  autoComplete="off"
                  disabled
                />
              </div>
              <button type="submit" className="avds-btn avds-btn--secondary avds-btn--lg login-submit" disabled>
                {t("login.signIn")}
              </button>
              <p className="login-note">{t("login.partnerUnavailable")}</p>
            </form>
          </section>

          <p className="login-secured">
            {t("login.securedPrefix")}
            <a href="#">{t("login.needHelp")}</a>
          </p>
        </div>
      </div>

      <div className="login-aside">
        <div className="login-aside__bg" />
        <img
          src="/logo-white.png"
          alt={t("login.brandAlt")}
          style={{ width: "280px", maxWidth: "80%", position: "relative" }}
        />
        <div className="login-aside__quote">
          {t("login.quoteBefore")}
          <em>{t("login.quoteAccent")}</em>
          {t("login.quoteAfter")}
        </div>
        <div className="login-aside__foot">{t("login.asideFoot")}</div>
      </div>
    </div>
  );
}
